// Sistema de toasts / notificaciones efímeras — sistema de diseño
// "Audiophile Precision".
//
// Antes TODA la retroalimentación era inline (AlertMessage o banners locales de
// éxito), que el usuario podía no ver si estaba desplazado en otra parte del
// scroll. Este proveedor ofrece una confirmación uniforme y perceptible para
// acciones puntuales (guardar, sincronizar, exportar, enviar factura…).
//
// Accesibilidad:
//  - La región de toasts es un live-region con `aria-live` (polite para
//    info/éxito, assertive para error) para que los lectores de pantalla la
//    anuncien sin robar el foco.
//  - Se auto-descartan tras un tiempo; el temporizador se PAUSA al pasar el
//    puntero o enfocar un toast (para poder leerlo/accionarlo).
//  - La animación de entrada respeta `prefers-reduced-motion`.
//
// Se monta por ENCIMA de las capas pública y privada (ver App.tsx) para que
// cualquier vista pueda emitir un toast con `useToast()`.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './components.js';
import { haptic } from './haptics.js';

export type ToastTone = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
  /** Duración en ms antes del auto-descarte (0 = no auto-descartar). */
  duration: number;
}

interface ToastApi {
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  /** Descarta un toast por id (uso interno/manual). */
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Hook para emitir toasts desde cualquier vista. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast debe usarse dentro de <ToastProvider>.');
  }
  return ctx;
}

const DEFAULT_DURATION = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((tone: ToastTone, message: string, duration = DEFAULT_DURATION) => {
    if (!message || !message.trim()) return;
    const id = nextId.current++;
    setToasts((list) => [...list, { id, tone, message, duration }]);
    // Haptic sutil acorde al tono (mejora progresiva; no-op sin soporte).
    haptic(tone === 'error' ? 'error' : tone === 'success' ? 'success' : 'light');
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (m, d) => push('success', m, d),
      error: (m, d) => push('error', m, d),
      info: (m, d) => push('info', m, d),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

/** Contenedor fijo de los toasts (live-region). */
function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  if (typeof document === 'undefined') return null;
  // Se renderiza por portal para quedar por encima del layout sin heredar
  // `overflow`/`transform` de contenedores padres. Posición inferior centrada,
  // respetando la safe-area de iOS para no chocar con la tab-bar.
  return createPortal(
    <div
      className="fixed inset-x-0 bottom-0 z-[55] flex flex-col items-center gap-space-xs px-margin-mobile pb-[calc(env(safe-area-inset-bottom,0px)+5rem)] pointer-events-none"
      // La región anuncia los mensajes; el tono assertive se maneja por toast.
      aria-live="polite"
      role="status"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

const TONE_STYLE: Record<ToastTone, { cls: string; icon: string }> = {
  success: {
    cls: 'bg-surface-deck border-goldmine-mint/50 text-goldmine-mint',
    icon: 'check_circle',
  },
  error: { cls: 'bg-surface-deck border-error/50 text-error', icon: 'error' },
  info: { cls: 'bg-surface-deck border-primary-container/50 text-primary', icon: 'info' },
};

function ToastCard({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const { t } = useTranslation();
  const { cls, icon } = TONE_STYLE[toast.tone];
  // Pausa del auto-descarte al pasar el puntero: se guarda el tiempo restante y
  // se reanuda al salir, para que el usuario pueda leer/accionar sin que el
  // toast desaparezca.
  const [paused, setPaused] = useState(false);

  // Auto-descarte. Depende de `paused`: al pausar, se limpia el timer; al
  // reanudar, se re-arma. Incluimos todas las dependencias (sin desactivar
  // la regla de hooks): `toast.id`/`duration` son estables por toast.
  useEffect(() => {
    if (toast.duration <= 0 || paused) return;
    const timer = setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => clearTimeout(timer);
  }, [paused, toast.id, toast.duration, onDismiss]);

  const prefersReduced =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`pointer-events-auto w-full max-w-md flex items-start gap-space-sm rounded-xl border px-space-md py-space-sm shadow-[0_8px_32px_rgba(0,0,0,0.5)] backdrop-blur-xl ${cls} ${
        prefersReduced ? '' : 'animate-[toast-in_180ms_ease-out]'
      }`}
    >
      <Icon name={icon} className="text-[20px] shrink-0 mt-px" />
      <span className="flex-1 min-w-0 font-body-sm text-body-sm text-on-surface break-words">
        {toast.message}
      </span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('common.close')}
        className="shrink-0 w-7 h-7 -mr-1 rounded flex items-center justify-center text-on-surface-variant hover:text-on-surface"
      >
        <Icon name="close" className="text-[18px]" />
      </button>
    </div>
  );
}
